'use client';

import type { Condition } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { useReducer, useRef, type PropsWithChildren } from 'react';

import { ViewParametersContext } from '@/contexts/navigation/viewParametersContext';
import {
  createCombineCondition,
  isValueCondition,
} from '@/types/advanceFilter';

import { deserializeSearchParams } from './serialization';

const normalizeFilter = (filter: Condition | undefined) => {
  if (!filter) {
    return undefined;
  }

  if (isValueCondition(filter)) {
    return createCombineCondition([filter]);
  }

  return !filter.conditions?.length ? undefined : filter;
};

export const ViewParametersProvider = ({ children }: PropsWithChildren) => {
  const searchParams = useSearchParams();
  const deserializedSearchParams = deserializeSearchParams(searchParams);
  const params = useRef(deserializedSearchParams);

  const [version, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const setParams = (newParams: Record<string, unknown>, render: boolean) => {
    if (newParams?.query) {
      newParams.query = normalizeFilter(newParams.query as Condition);
    }

    if (
      typeof newParams.pageIndex === 'number' &&
      params.current.query !== newParams.query
    ) {
      newParams.pageIndex = 0;
    }

    params.current = newParams;
    if (render) {
      forceUpdate();
    }
  };

  return (
    <ViewParametersContext.Provider
      value={{
        version,
        params: () => params.current,
        setInitialValues: (initialValues) => {
          setParams(initialValues, true);
        },
        setParamsWithoutRender: (newParams) => {
          setParams(newParams, false);
        },
        setParams: (p) => {
          const newParams =
            typeof p === 'function' ? p(params.current || {}) : p;
          setParams(newParams, true);
        },
      }}
    >
      {children}
    </ViewParametersContext.Provider>
  );
};
